import _ from '@/common/helpers';
import {ACTION_LIST, ACTION_METHOD_MAP, ACTIONS} from './definitions/actions-methods';

/**
 * Uses heuristics to check if an user is admin
 * based on its permissions.
 * @param {Array} methodPermissions - in API form
 * */
export function isAdmin(methodPermissions) {
    return methodPermissions === null
        || (Array.isArray(methodPermissions)
            && methodPermissions.length === 1
            && methodPermissions[0].resourceName === null
            && methodPermissions[0].methods === null);
}

/**
 * Transform resources name from kebab case to camel case.
 * @param {string} resourceName
 */
function getResourceKey(resourceName) {
    return resourceName.split('-')
        .map((word, index) => (index > 0 ? _.capitalize(word) : word)).join('');
}

/**
 * Transform resources keys from camel case to kebab case. Uses the provided list as reference
 * or generates one from the string if not found.
 * @param {string} resourceKey
 * @param {{}} resources an object containing a map of camelCase to kebab-case values for
 * resource names
 */
function getResourceName(resourceKey, resources = {}) {
    if (Reflect.has(resources, resourceKey)) {
        return resources[resourceKey];
    }
    return resourceKey
        .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
        .toLowerCase();
}

/**
 * Transform methods permissions to action permissions
 * Methods permissions
 * ['GET', 'PUT' , 'DELETE']
 * Becomes Actions permissions
 * ['delete', 'read']
 *
 * @param {Array} methods - in the API form
 * @return {Array} actions
 */
export function methodsToActions(methods) {
    if (methods === null) {
        // null means `all` access
        return [ACTIONS.all];
    }
    if (!methods || !methods.length) {
        // a falsy value or an empty array should be no access
        return [];
    }
    return ACTION_LIST.slice(1).filter(
        action => ACTION_METHOD_MAP[action].every(
            method => methods.indexOf(method) !== -1,
        ),
    );
}

/**
 * Transform the permission array from method based to action based object
 * [{resourceName: 'resourceName', methods:['GET']}]
 * {resourceName: ['read']}
 *
 * @param {Array} methodPermissions - in the API form
 * @return {Object} actionsPermissions
 */
export function methodToActionPermissions(methodPermissions) {
    if (!methodPermissions) {
        return {};
    }
    return methodPermissions.reduce(
        (actionPermissions, {resourceName, methods}) => (
            {
                ...actionPermissions,
                [getResourceKey(resourceName)]: methodsToActions(methods),
            }), {},
    );
}

/**
 * Transform action permissions to method permissions
 * // Action permissions
 * ['delete', 'read']
 * // Becomes Method permissions
 * ['GET', 'PUT' , 'DELETE']
 * @param {Array} actions
 * @return {Array} methods - in the API form
 */
export function actionsToMethods(actions) {
    if (!actions || !actions.length) {
        return [];
    }
    if (actions.indexOf(ACTIONS.all) !== -1) {
        return null;
    }
    return [].concat(...actions.map(action => ACTION_METHOD_MAP[action]))
        .filter((item, index, array) => array.indexOf(item) === index);
}

/**
 * Transform the permission object from action based to method based array
 * {resourceName: ['read']}
 * [{resourceName: 'resource-name', methods:['GET']}]
 *
 * @param {Array} actionsPermissions
 * @param {{}} resources an object mapping camelCase to kebab-case for all resource names
 * @return {Array} methodPermissions
 */
export function actionToMethodPermissions(actionsPermissions, resources) {
    if (!actionsPermissions) {
        return null;
    }
    const resourceNames = Object.keys(actionsPermissions);
    return resourceNames.map(resourceName => ({
        resourceName: getResourceName(resourceName, resources),
        methods: actionsToMethods(actionsPermissions[resourceName]),
    }));
}
